import {
	DocMdp,
	MetadataInformation, Security,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogType from "./PortalDialogType";
import PortalDocument from "../document/PortalDocument";
import FunctionHelper from "../../shared/helper/FunctionHelper";

export enum Restriction {
	canAssemble,
	canExtractContent,
	canExtractForAccessibility,
	canFillInForm,
	canModify,
	canModifyAnnotations,
	canPrint,
	canPrintHighRes
}

export enum Intention {
	reading,
	editingContent,
	editingAnnotations,
	signingDocuments,
	fillingInForms
}

class PortalDialogSecurity {
	private static _instance: PortalDialogSecurity;
	private _securityRestrictions: Map<PortalDialogType, Array<Restriction>>;
	private _signatureRestrictions: Map<SignatureEditProfile, Map<Intention, Map<DocMdp, boolean>>>;

	private constructor() {
		this._securityRestrictions = new Map<PortalDialogType, Array<Restriction>>([
			[PortalDialogType.ROTATE, [Restriction.canModify]],
			[PortalDialogType.ORIENTATION, [Restriction.canModify]],
			[PortalDialogType.PRINT, [Restriction.canPrint]],
			[PortalDialogType.DELETE_PAGE, [Restriction.canAssemble]],
			[PortalDialogType.SPLIT, [Restriction.canAssemble]],
			[PortalDialogType.WATERMARK, [Restriction.canModify]],
			[PortalDialogType.ATTACHMENT_ADD, [Restriction.canModify, Restriction.canModifyAnnotations]],
			[PortalDialogType.ATTACHMENT_ADD_GLOBAL, [Restriction.canModify]],
			[PortalDialogType.ATTACHMENT_EXTRACT, [Restriction.canExtractContent]],
			[PortalDialogType.ATTACHMENT_DELETE, [Restriction.canModify]],
			[PortalDialogType.EXTRACT_CONTENT, [Restriction.canExtractContent]],
			[PortalDialogType.EXTRACT_IMAGES, [Restriction.canExtractContent]],
			[PortalDialogType.EXTRACT_INFO, [Restriction.canExtractContent]],
			[PortalDialogType.EXTRACT_PAGE, [Restriction.canExtractContent]],
			[PortalDialogType.BACKGROUND, [Restriction.canModify]],
			[PortalDialogType.HEADER_FOOTER, [Restriction.canModify]],
			[PortalDialogType.DESCRIPTION, [Restriction.canModify]],
			[PortalDialogType.VIEW, [Restriction.canModify]],
			[PortalDialogType.BOXES, [Restriction.canModify]],
			[PortalDialogType.TRANSITION, [Restriction.canModify]],
			[PortalDialogType.ADD_PAGE, [Restriction.canAssemble]],
			[PortalDialogType.PDFA, [Restriction.canModify, Restriction.canExtractContent]],
			[PortalDialogType.OCR, [Restriction.canModify, Restriction.canExtractContent]],
			[PortalDialogType.SIGNATURE, [Restriction.canModify, Restriction.canModifyAnnotations]],
			[PortalDialogType.FLATTEN_FORMS, [Restriction.canModify]],
			[PortalDialogType.EXTRACT_FORMS, [Restriction.canExtractContent]],
			[PortalDialogType.IMPORT_FORMS, [Restriction.canFillInForm]],
			[PortalDialogType.EDIT_FORMS, [Restriction.canFillInForm]],
			[PortalDialogType.SANITIZE, [Restriction.canModify]],
			[PortalDialogType.REDACT_AREA, [Restriction.canModify]],
			[PortalDialogType.REDACT_TEXT, [Restriction.canModify]],
			[PortalDialogType.REDACT_PAGE, [Restriction.canModify]],
			[PortalDialogType.REDACT, [Restriction.canModify]],
			[PortalDialogType.ADD_OUTLINE, [Restriction.canModify]],
			[PortalDialogType.REMOVE_OUTLINE, [Restriction.canModify]],
			[PortalDialogType.BARCODE_CREATE, [Restriction.canModify]],
			[PortalDialogType.BARCODE_CREATE_AREA, [Restriction.canModify]],
			[PortalDialogType.BARCODE_CREATE_SWISS_QR, [Restriction.canModify]],
			[PortalDialogType.BARCODE_CREATE_SWISS_QR_AREA, [Restriction.canModify]],
			[PortalDialogType.BARCODE_DETECT, [Restriction.canExtractContent]],
			[PortalDialogType.BARCODE_SWISS_QR_CODE_FORM, [Restriction.canModify]],
			[PortalDialogType.ANNOTATION_NOTE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_NOTE_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_MARK, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_MARK_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_STRIKE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_STRIKE_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_UNDERLINE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_UNDERLINE_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_SQUIGGLY, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_SQUIGGLY_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_LINE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_LINE_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_CIRCLE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_CIRCLE_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_SQUARE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_SQUARE_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_STAMP, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_STAMP_EDIT, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_DELETE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_REPLY, [Restriction.canModifyAnnotations]],
			[PortalDialogType.ANNOTATION_FLATTEN, [Restriction.canModify]],
			[PortalDialogType.ATTACHMENT_DELETE, [Restriction.canModifyAnnotations]],
			[PortalDialogType.MERGE, [Restriction.canAssemble]],
			[PortalDialogType.PORTFOLIO_ADD_FILE, [Restriction.canModify]],
			[PortalDialogType.PORTFOLIO_ADD_FOLDER, [Restriction.canModify]],
			[PortalDialogType.PORTFOLIO_REMOVE, [Restriction.canModify]],
			[PortalDialogType.PORTFOLIO_EXTRACT, [Restriction.canExtractContent]],
			[PortalDialogType.ENCRYPT, [Restriction.canModify]],
			[PortalDialogType.CERTIFICATE_ENCRYPT, [Restriction.canModify]],
			[PortalDialogType.DECRYPT, [Restriction.canModify]],
			[PortalDialogType.MOVE, [Restriction.canModify, Restriction.canAssemble]],
			[PortalDialogType.COMPRESS, [Restriction.canModify]],
			[PortalDialogType.SCALE, [Restriction.canModify]],
			[PortalDialogType.EXTRACT_HTML, [Restriction.canExtractContent]],
			[PortalDialogType.SIGNATURE_REMOVE, [Restriction.canModify]]
		]);

		this._signatureRestrictions = new Map<SignatureEditProfile, Map<Intention, Map<DocMdp, boolean>>>([
			[
				SignatureEditProfile.GeneralEditRestrictions,
				new Map<Intention, Map<DocMdp, boolean>>([
					[
						Intention.reading,
						new Map<DocMdp, boolean>([
							[DocMdp.None, true],
							[DocMdp.NoChangesAllowed, true],
							[DocMdp.FormFillingSignatures, true],
							[DocMdp.FormFillingSignaturesAnnotations, true]
						])
					],
					[
						Intention.editingAnnotations,
						new Map<DocMdp, boolean>([
								[DocMdp.None, false],
								[DocMdp.NoChangesAllowed, false],
								[DocMdp.FormFillingSignatures, false],
								[DocMdp.FormFillingSignaturesAnnotations, false]
							]
						)
					],
					[
						Intention.editingContent,
						new Map<DocMdp, boolean>(
							[
								[DocMdp.None, false],
								[DocMdp.NoChangesAllowed, false],
								[DocMdp.FormFillingSignatures, false],
								[DocMdp.FormFillingSignaturesAnnotations, false]
							]
						)
					],
					[
						Intention.fillingInForms,
						new Map<DocMdp, boolean>(
							[
								[DocMdp.None, false],
								[DocMdp.NoChangesAllowed, false],
								[DocMdp.FormFillingSignatures, false],
								[DocMdp.FormFillingSignaturesAnnotations, false]
							]
						)
					],
					[
						Intention.signingDocuments,
						new Map<DocMdp, boolean>(
							[
								[DocMdp.None, false],
								[DocMdp.NoChangesAllowed, false],
								[DocMdp.FormFillingSignatures, false],
								[DocMdp.FormFillingSignaturesAnnotations, false]
							]
						)
					]
				])
			],
			[
				SignatureEditProfile.AdobeRestrictions,
				new Map<Intention, Map<DocMdp, boolean>>(
					[
						[
							Intention.reading,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, true],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.editingAnnotations,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, false],
									[DocMdp.FormFillingSignatures, false],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.editingContent,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, false],
									[DocMdp.NoChangesAllowed, false],
									[DocMdp.FormFillingSignatures, false],
									[DocMdp.FormFillingSignaturesAnnotations, false]
								]
							)
						],
						[
							Intention.fillingInForms,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, false],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.signingDocuments,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, false],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						]
					]
				)
			],
			[
				SignatureEditProfile.NoEditRestrictions,
				new Map<Intention, Map<DocMdp, boolean>>(
					[
						[
							Intention.reading,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, true],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.editingAnnotations,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, true],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.editingContent,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, true],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.fillingInForms,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, true],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						],
						[
							Intention.signingDocuments,
							new Map<DocMdp, boolean>(
								[
									[DocMdp.None, true],
									[DocMdp.NoChangesAllowed, true],
									[DocMdp.FormFillingSignatures, true],
									[DocMdp.FormFillingSignaturesAnnotations, true]
								]
							)
						]
					]
				)
			]
		]);
	}

	public checkRestrictions(
		dialogType: PortalDialogType, profile: SignatureEditProfile, intention: Intention,
		portalDocument: PortalDocument
	): boolean {
		let result: boolean = true;

		if (portalDocument.hasSignature() && FunctionHelper.applyValue(
			portalDocument.metadata?.information?.signatures, MetadataInformation.getSignaturesDefault()
		) > 0) {
			result = false;

			let signatureRestriction: boolean | undefined =
				this.signatureRestrictions.get(profile)?.get(intention)?.get(
					FunctionHelper.applyValue(
						portalDocument.metadata?.information?.docMDP, MetadataInformation.getDocMDPDefault()
					)
				);

			if (FunctionHelper.exists(signatureRestriction)) {
				result = signatureRestriction!;
			}
		}

		if (portalDocument.hasCertificateProtection()) {
			let securityRestrictions: Array<Restriction> = FunctionHelper.applyValue(
				this.securityRestrictions.get(dialogType), []
			);

			for (let restriction of securityRestrictions!) {
				if (!result) {
					break;
				}

				switch (restriction) {
					case Restriction.canModify:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
						);
						break;
					case Restriction.canAssemble:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canAssemble, Security.getCanAssembleDefault()
						);
						break;
					case Restriction.canExtractContent:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
						);
						break;
					case Restriction.canExtractForAccessibility:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canExtractForAccessibility,
							Security.getCanExtractForAccessibilityDefault()
						);
						break;
					case Restriction.canFillInForm:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canFillInForm, Security.getCanFillInFormDefault()
						);
						break;
					case Restriction.canModifyAnnotations:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canModifyAnnotations,
							Security.getCanModifyAnnotationsDefault()
						);
						break;
					case Restriction.canPrint:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canPrint, Security.getCanPrintDefault()
						);
						break;
					case Restriction.canPrintHighRes:
						result = FunctionHelper.applyValue(
							portalDocument.metadata?.security?.canPrintHighRes, Security.getCanPrintHighResDefault()
						);
						break;
				}
			}
		}

		return result;
	}

	get signatureRestrictions(): Map<SignatureEditProfile, Map<Intention, Map<DocMdp, boolean>>> {
		return this._signatureRestrictions;
	}

	get securityRestrictions(): Map<PortalDialogType, Array<Restriction>> {
		return this._securityRestrictions;
	}

	public static get instance(): PortalDialogSecurity {
		return FunctionHelper.applyValue(this._instance, (this._instance = new this()));
	}
}

export default PortalDialogSecurity.instance;